import { ListResponse } from "store/api/types";
import { getCsrfToken, getAuthToken } from "utils/api-utils";

/**
 *
 * @param path
 * @param authToken
 * @param options
 * @returns
 */
function buildRequestUrl(path: string): string {
  if (path.startsWith("http")) {
    return path;
  }

  const hostUrl = process.env.NEXT_PUBLIC_HOST_URL ?? "";
  if (!hostUrl) {
    return path;
  }

  const normalizedHostUrl = hostUrl.endsWith("/")
    ? hostUrl.slice(0, -1)
    : hostUrl;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${normalizedHostUrl}${normalizedPath}`;
}

export function fetchClient(path: string, options?: RequestInit) {
  const url = buildRequestUrl(path);

  const csrfToken = getCsrfToken();
  const authToken = getAuthToken();

  const newOptions = {
    ...options,
    credentials: "include",
    headers: {
      ...options?.headers,
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
      ...(authToken ? { Authorization: `Token ${authToken}` } : {}),
    },
  } as RequestInit;

  const retryFetch = async (attempt = 1): Promise<Response> => {
    try {
      const response = await fetch(url, newOptions);
      if (response.status === 401) {
        throw new Error("401");
      }
      if (!response.ok && attempt < 3) {
        const delay = Math.pow(2, attempt) * 100; // exponential backoff
        await new Promise((resolve) => setTimeout(resolve, delay));
        return retryFetch(attempt + 1);
      }
      return response;
    } catch (error) {
      if (attempt < 3) {
        const delay = Math.pow(2, attempt) * 100; // exponential backoff
        await new Promise((resolve) => setTimeout(resolve, delay));
        return retryFetch(attempt + 1);
      }
      throw error;
    }
  };

  return retryFetch();
}

export async function fetchAll<T>(
  baseUrl: string,
  callback?: (items: T[]) => void,
): Promise<T[]> {
  async function fetchItems(url: string, items: T[]): Promise<T[]> {
    if (!url) return items;

    const response: ListResponse<T> = await fetchClient(url).then(
      (res) => res.json() as Promise<ListResponse<T>>,
    );
    callback?.(response.results);
    const newItems = [...items, ...response.results];

    return fetchItems(response.next, newItems);
  }

  return fetchItems(baseUrl, []);
}
